import { TimeEntry, PaginatedResponse, WeeklyEntries } from '../types'
import { apiRequestWithAutoRefresh } from '../utils/apiUtils'

export const timerService = {
  // 取得計時記錄列表
  async getTimeEntries(params?: {
    page?: number
    limit?: number
    projectId?: string
    status?: string
    startDate?: string
    endDate?: string
  }): Promise<PaginatedResponse<TimeEntry>> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.projectId) searchParams.append('projectId', params.projectId)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.startDate) searchParams.append('startDate', params.startDate)
    if (params?.endDate) searchParams.append('endDate', params.endDate)

    const result = await apiRequestWithAutoRefresh(`/time-entries?${searchParams.toString()}`, {
      method: 'GET'
    })

    return {
      data: result.data || [],
      pagination: (result as any).pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    }
  },

  // 取得進行中的計時
  async getActiveTimer(): Promise<TimeEntry | null> {
    const result = await apiRequestWithAutoRefresh('/time-entries/active', {
      method: 'GET'
    })
    return result.data
  },

  // 開始計時
  async startTimer(data: {
    projectId: string
    description?: string
  }): Promise<TimeEntry> {
    const result = await apiRequestWithAutoRefresh('/time-entries/start', {
      method: 'POST',
      body: JSON.stringify(data)
    })
    return result.data
  },

  // 停止計時
  async stopTimer(timeEntryId: string, description?: string): Promise<TimeEntry> {
    const body = description !== undefined ? { description } : undefined
    
    const result = await apiRequestWithAutoRefresh(`/time-entries/${timeEntryId}/stop`, {
      method: 'PUT',
      ...(body && { body: JSON.stringify(body) })
    })
    return result.data
  },

  // 更新計時記錄
  async updateTimeEntry(
    timeEntryId: string,
    data: {
      description?: string
      projectId?: string
      startTime?: string
      endTime?: string
      isManual?: boolean
      manualReason?: string
    }
  ): Promise<TimeEntry> {
    const result = await apiRequestWithAutoRefresh(`/time-entries/${timeEntryId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return result.data
  },

  // 刪除計時記錄
  async deleteTimeEntry(timeEntryId: string): Promise<void> {
    await apiRequestWithAutoRefresh(`/time-entries/${timeEntryId}`, {
      method: 'DELETE'
    })
  },

  // 取得今日計時統計（支援時區）
  async getTodayEntries(): Promise<{
    entries: TimeEntry[]
    totalDuration: number
    totalHours: number
    totalMinutes: number
  }> {
    // 直接添加時區參數到查詢字符串
    const searchParams = new URLSearchParams()
    searchParams.set('timezoneOffset', new Date().getTimezoneOffset().toString())
    
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      searchParams.set('timezone', timezone)
    } catch (error) {
      console.warn('Cannot detect timezone, using offset only:', error)
    }
    
    const result = await apiRequestWithAutoRefresh(`/time-entries/today?${searchParams.toString()}`, {
      method: 'GET'
    })
    return result.data
  },

  // 取得過去7天計時統計（支援時區）
  async getWeeklyEntries(): Promise<{
    weeklyEntries: WeeklyEntries
    totalDuration: number
    totalHours: number
    totalMinutes: number
  }> {
    const searchParams = new URLSearchParams()
    searchParams.set('timezoneOffset', new Date().getTimezoneOffset().toString())
    
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      searchParams.set('timezone', timezone)
    } catch (error) {
      console.warn('Cannot detect timezone, using offset only:', error)
    }
    
    const result = await apiRequestWithAutoRefresh(`/time-entries/weekly?${searchParams.toString()}`, {
      method: 'GET'
    })
    return result.data
  },

  // 手動新增時間記錄
  async createManualTimeEntry(data: {
    projectId: string
    description?: string
    startTime: string
    endTime: string
    date: string
    manualReason: string
  }): Promise<TimeEntry> {
    // 將日期和時間結合成完整的 ISO 字符串
    const startDateTime = new Date(`${data.date}T${data.startTime}:00`).toISOString()
    const endDateTime = new Date(`${data.date}T${data.endTime}:00`).toISOString()
    
    const result = await apiRequestWithAutoRefresh('/time-entries/manual', {
      method: 'POST',
      body: JSON.stringify({
        projectId: data.projectId,
        description: data.description || '',
        startTime: startDateTime,
        endTime: endDateTime,
        isManual: true,
        manualReason: data.manualReason
      })
    })
    return result.data
  },

  // 編輯時間記錄
  async editTimeEntry(data: {
    id: string
    projectId: string
    description: string
    startTime: string
    endTime: string
    originalDate: string
  }): Promise<TimeEntry> {
    // 正確處理用戶本地時間，不強制轉換為 UTC
    const startDateTime = new Date(`${data.originalDate}T${data.startTime}:00`).toISOString()
    const endDateTime = new Date(`${data.originalDate}T${data.endTime}:00`).toISOString()
    
    // 添加時區信息到請求
    const searchParams = new URLSearchParams()
    searchParams.set('timezoneOffset', new Date().getTimezoneOffset().toString())
    
    const result = await apiRequestWithAutoRefresh(`/time-entries/${data.id}?${searchParams.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({
        projectId: data.projectId,
        description: data.description,
        startTime: startDateTime,
        endTime: endDateTime
      })
    })
    return result.data
  }
}

// 時間格式化工具函數
export const timeUtils = {
  // 將秒數轉換為小時數，精確到小數點後兩位
  convertSecondsToHours(seconds: number): number {
    if (seconds <= 0) return 0
    return Math.round((seconds / 3600) * 100) / 100
  },

  // 將小時數轉換為秒數
  convertHoursToSeconds(hours: number): number {
    if (hours <= 0) return 0
    return Math.round(hours * 3600)
  },

  // 將秒數轉換為 HH:MM:SS 格式
  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  },

  // 將秒數轉換為人類可讀格式
  formatDurationHuman(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (hours > 0) {
      return `${hours}小時${minutes > 0 ? ` ${minutes}分鐘` : ''}`
    } else if (minutes > 0) {
      return `${minutes}分鐘`
    } else {
      return '不到1分鐘'
    }
  },

  // 格式化工時為字串顯示（保留兩位小數）
  formatHoursDisplay(seconds: number): string {
    const hours = this.convertSecondsToHours(seconds)
    return hours.toFixed(2)
  },

  // 計算薪資金額，基於精確工時
  calculateSalary(seconds: number, hourlyRate: number): number {
    const hours = this.convertSecondsToHours(seconds)
    return Math.round(hours * hourlyRate)
  },

  // 計算兩個時間之間的秒數差
  calculateDuration(startTime: string, endTime?: string): number {
    const start = new Date(startTime).getTime()
    const end = endTime ? new Date(endTime).getTime() : Date.now()
    return Math.floor((end - start) / 1000)
  },

  // 格式化日期時間
  formatDateTime(dateTime: string): string {
    return new Date(dateTime).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  },

  // 格式化時間
  formatTime(dateTime: string): string {
    return new Date(dateTime).toLocaleString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit'
    })
  },

  // 格式化日期
  formatDate(dateTime: string): string {
    return new Date(dateTime).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  },

  // 檢查是否為今天
  isToday(dateTime: string): boolean {
    const date = new Date(dateTime)
    const today = new Date()
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    )
  },

  // 取得今天的開始時間
  getTodayStart(): Date {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  },

  // 取得今天的結束時間
  getTodayEnd(): Date {
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    return today
  }
}
